import React from 'react';
import styled from 'styled-components';
import { scroller } from 'react-scroll';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { InstagramIcon, ArrowUpIcon, ByteForgeLogo } from './IconComponents';
import { useTranslation, Trans } from 'react-i18next';
import { openConsentSettings } from '../consent';

const FooterSection = styled.footer`
  background-color: var(--dark-background);
  color: var(--light-text-color);
  padding: 4rem 0 2rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterInfo = styled.div``;

const FooterLogo = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;

  span {
    color: var(--accent-color);
  }
`;

const FooterDescription = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--light-text-color);
  transition: var(--transition);

  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-3px);
  }
`;

const FooterLinks = styled.div``;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: var(--primary-color);
  }
`;

const FooterLinksList = styled.ul`
  list-style: none;
`;

const FooterLinkItem = styled.li`
  margin-bottom: 0.75rem;
`;

const FooterLink = styled.a`
  color: var(--light-text-color);
  font-size: 0.95rem;
  transition: var(--transition);
  cursor: pointer;

  &:hover {
    color: var(--accent-color);
    padding-left: 5px;
  }
`;

const ExternalLink = styled.a`
  color: var(--light-text-color);
  font-size: 0.95rem;
  transition: var(--transition);

  &:hover {
    color: var(--accent-color);
    padding-left: 5px;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 0.9rem;
`;

const ScrollToTop = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--accent-color);
    transform: translateY(-3px);
  }
`;

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCookieSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openConsentSettings();
  };

  const handleSectionLink = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const scrollOptions = { spy: true, smooth: true, offset: -70, duration: 500 };

    if (location.pathname === '/') {
      scroller.scrollTo(sectionId, scrollOptions);
    } else {
      navigate('/');
      setTimeout(() => {
        scroller.scrollTo(sectionId, scrollOptions);
      }, 100);
    }
  };

  return (
    <FooterSection>
      <FooterContainer>
        <FooterTop>
          <FooterInfo>
            <FooterLogo>
              <ByteForgeLogo />
            </FooterLogo>
            <FooterDescription>
              {t('footer.description')}
            </FooterDescription>
            {/*<SocialLinks>*/}
            {/*  <SocialLink href="#" target="_blank" rel="noopener noreferrer">*/}
            {/*  </SocialLink>*/}
            {/*  <SocialLink href="#" target="_blank" rel="noopener noreferrer">*/}
            {/*    <InstagramIcon />*/}
            {/*  </SocialLink>*/}
            {/*</SocialLinks>*/}
          </FooterInfo>

          <FooterLinks>
            <FooterTitle>{t('footer.sections.quick_links')}</FooterTitle>
            <FooterLinksList>
              <FooterLinkItem>
                <FooterLink href="/#home" onClick={handleSectionLink('home')}>
                  {t('navigation.home')}
                </FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink href="/#services" onClick={handleSectionLink('services')}>
                  {t('navigation.services')}
                </FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink href="/#about" onClick={handleSectionLink('about')}>
                  {t('navigation.about')}
                </FooterLink>
              </FooterLinkItem>
              <FooterLinkItem>
                <FooterLink href="/#contact" onClick={handleSectionLink('contact')}>
                  {t('navigation.contact')}
                </FooterLink>
              </FooterLinkItem>
            </FooterLinksList>
          </FooterLinks>

          {/*<FooterLinks>*/}
          {/*  <FooterTitle>{t('footer.sections.services')}</FooterTitle>*/}
          {/*  <FooterLinksList>*/}
          {/*    <FooterLinkItem>*/}
          {/*      <ExternalLink href="#">{t('footer.service_links.web_development')}</ExternalLink>*/}
          {/*    </FooterLinkItem>*/}
          {/*    <FooterLinkItem>*/}
          {/*      <ExternalLink href="#">{t('footer.service_links.mobile_app_development')}</ExternalLink>*/}
          {/*    </FooterLinkItem>*/}
          {/*    <FooterLinkItem>*/}
          {/*      <ExternalLink href="#">{t('footer.service_links.custom_software')}</ExternalLink>*/}
          {/*    </FooterLinkItem>*/}
          {/*    <FooterLinkItem>*/}
          {/*      <ExternalLink href="#">{t('footer.service_links.database_solutions')}</ExternalLink>*/}
          {/*    </FooterLinkItem>*/}
          {/*    <FooterLinkItem>*/}
          {/*      <ExternalLink href="#">{t('footer.service_links.cloud_services')}</ExternalLink>*/}
          {/*    </FooterLinkItem>*/}
          {/*  </FooterLinksList>*/}
          {/*</FooterLinks>*/}

          <FooterLinks>
            <FooterTitle>{t('footer.sections.legal')}</FooterTitle>
            <FooterLinksList>
              <FooterLinkItem>
                <ExternalLink as={Link} to="/datenschutz">{t('footer.legal_links.privacy_policy')}</ExternalLink>
              </FooterLinkItem>
              {/*<FooterLinkItem>*/}
              {/*  <ExternalLink href="#">{t('footer.legal_links.terms_of_service')}</ExternalLink>*/}
              {/*</FooterLinkItem>*/}
              {/*<FooterLinkItem>*/}
              {/*  <ExternalLink href="#" onClick={handleCookieSettingsClick}>{t('footer.legal_links.cookie_policy')}</ExternalLink>*/}
              {/*</FooterLinkItem>*/}
              <FooterLinkItem>
                <ExternalLink as={Link} to="/impressum">{t('footer.legal_links.imprint')}</ExternalLink>
              </FooterLinkItem>
            </FooterLinksList>
          </FooterLinks>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            {t('footer.copyright', { year: currentYear })}
          </Copyright>
          <ScrollToTop href="/#home" onClick={handleSectionLink('home')}>
            <ArrowUpIcon />
          </ScrollToTop>
        </FooterBottom>
      </FooterContainer>
    </FooterSection>
  );
};

export default Footer;
